import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  armDockViewportForPortalEntry,
  recoverMobileViewportAfterPortalEntry,
} from '../lib/mobileViewport';
import {
  goToPortal,
  IS_UNIFIED_PORTAL,
  portalHomePath,
  setChooserVerifyingHold,
  type PortalKind,
} from '../lib/portal';
import { ensureSecondaryAuthSynced } from '../lib/supabase';

function needsSecondaryJwt(kind: PortalKind): boolean {
  return kind === 'dem' || kind === 'forms' || kind === 'it';
}

/** Two frames so Verifying can paint (and later so destination can settle). */
function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

/**
 * Soft SPA switch.
 * Choose-portal: one Verifying on the chooser, then allowed+navigate together —
 * destination must never mount under checking (that caused a second Verifying on mobile CRM).
 * PortalSwitcher: dem/it/forms wait on JWT; other portals navigate immediately.
 */
export function useGoToPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enterPortal, armPortalEntry } = useAuth();
  const switchingRef = useRef(false);

  return useCallback(
    async (kind: PortalKind) => {
      if (!IS_UNIFIED_PORTAL) {
        goToPortal(kind);
        return;
      }
      if (switchingRef.current) return;
      switchingRef.current = true;
      const fromChooser = location.pathname === '/choose-portal';
      const home = portalHomePath(kind);
      try {
        if (fromChooser) {
          // Hold blocks RequireAuth from painting a second Verifying on /crm.
          setChooserVerifyingHold(true);
          let armed = false;
          flushSync(() => {
            armed = armPortalEntry(kind);
          });
          if (!armed) {
            setChooserVerifyingHold(false);
            return;
          }

          await waitForPaint();
          if (needsSecondaryJwt(kind)) {
            await ensureSecondaryAuthSynced(4_000);
          }

          armDockViewportForPortalEntry();
          let entered = false;
          // allowed + navigate in one commit — no "allowed on chooser" gap, no
          // destination mount while still checking.
          flushSync(() => {
            entered = enterPortal(kind);
            if (!entered) return;
            navigate(home, { replace: true });
          });
          if (!entered) {
            setChooserVerifyingHold(false);
            document.documentElement.classList.remove('has-dock');
            return;
          }

          await waitForPaint();
          setChooserVerifyingHold(false);
          recoverMobileViewportAfterPortalEntry();
          return;
        }

        // PortalSwitcher — unchanged.
        if (needsSecondaryJwt(kind)) {
          let armed = false;
          flushSync(() => {
            armed = armPortalEntry(kind);
          });
          if (!armed) return;

          await ensureSecondaryAuthSynced(4_000);

          let entered = false;
          flushSync(() => {
            entered = enterPortal(kind);
            if (!entered) return;
            navigate(home, { replace: false });
          });
          if (!entered) return;
        } else {
          let entered = false;
          flushSync(() => {
            entered = enterPortal(kind);
            if (!entered) return;
            navigate(home, { replace: false });
          });
          if (!entered) return;
        }
      } finally {
        window.setTimeout(() => {
          switchingRef.current = false;
        }, 0);
      }
    },
    [armPortalEntry, enterPortal, navigate, location.pathname],
  );
}
